// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.OperatorValueComboTree.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Dropdown Tree Select Container component
 */

import { observer } from 'mobx-react';
import { action, observable, toJS, extendObservable, asReference } from 'mobx';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TreeSelect from 'share/components/controls/Izenda.TreeSelect';
import Input from 'share/components/controls/Izenda.Input';
import Select from 'share/components/controls/multiSelectBox/Izenda.Select';
import FloatAffixed from 'share/components/controls/Izenda.FloatAffixed';

import {
  map,
  isEmpty,
  invokeArgs,
  includes,
  forEach,
  isNil,
  remove,
  get,
  flatMap,
  reverse,
  head
} from 'lodash/fp';

const ALL_TEXT = '[All]';

const TreeDropDown = observer(function(props) {
  const {
    hideTreeView,
    applyFormater,
    handleChangeTreeSelect,
    disabled,
    observableObject: { isOpen, selectedItems },
    masterDom: { comboTreeDom },
    renderInContainer
  } = props;

  if (!isOpen) {
    return null;
  }
  return (
    <FloatAffixed
      anchor={comboTreeDom}
      onClickOutside={hideTreeView}
      onParentScroll={hideTreeView}
      renderInContainer={renderInContainer}>
      <TreeSelect
        data={selectedItems}
        applyFormater={applyFormater}
        onChange={handleChangeTreeSelect}
        disabled={disabled}
      />
    </FloatAffixed>
  );
});

const SelectedValue = observer(function(props) {
  const { onChangeSelect, disabled } = props;

  const displayedValues = map(item => {
    return { id: item.value, label: props.applyFormater(item.text) };
  }, this.props.observableObject.displayedValues);

  if (isEmpty(displayedValues)) {
    return null;
  }

  return (
    displayedValues.length > 0 && (
      <Select
        searchable={false}
        multi
        options={displayedValues}
        onChange={onChangeSelect}
        value={displayedValues}
        disabled={disabled}
        scalable={false}
        textboxMode
        valueKey='id'
        labelField='label'
        inputMaxHeight={50}
      />
    )
  );
});

@observer
export default class OperatorValueComboTree extends Component {
  static propTypes = {
    filterSource: PropTypes.object.isRequired,
    filterInfo: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.observableObject = observable({
      totalNodes: null,
      displayedValues: [],
      selectedItems: asReference([]),
      isOpen: false
    });
    this.tryGetData = false;
    this.dom = {};
    this.inititalTree(props);
  }

  componentWillReceiveProps(nextProps: any) {
    if (this.props.filterSource !== nextProps.filterSource) {
      this.inititalTree(nextProps);
    }
  }

  @action
  inititalTree(props: any) {
    const {
      filterSource,
      configField,
      store,
      filterInfo: { value }
    } = props;
    const data = toJS(filterSource);

    // TODO: need to delete block of code below when applying lazy load for tree
    if (!this.tryGetData && isEmpty(data)) {
      // try to get data for sub report (inherited filter)
      invokeArgs('getOperatorValueSource', [configField], store);
      this.tryGetData = true;
    }
    // END
    const includesAll = includes(ALL_TEXT, value);
    let counter = -1;

    const forEachNode = (nodes, parentText) => {
      forEach(node => {
        counter++;
        node.key = counter;

        forEachNode(node.nodes, node.value);

        const checked = includesAll || includes(node.value, value)
          || (parentText && includes(`${parentText}\\${node.value}`, value))
          || (node.nodes && node.nodes.length && this.isAllOptionSelected(node.nodes));
        extendObservable(node, { checked: checked });
      }, nodes);
    };

    forEachNode(data);

    if (data.length > 0) {
      let displayedValues = [];
      this.fillDisplayedValues(data, displayedValues);

      this.observableObject.totalNodes = counter;
      this.observableObject.displayedValues = displayedValues;
      this.observableObject.selectedItems = data;
    }
  }

  @action
  hideTreeView = () => {
    this.observableObject.isOpen = false;
  }

  @action
  handleShowTreeView = () => {
    this.observableObject.isOpen = !this.observableObject.isOpen;
  };

  /**
   * Find selected item on list filter value data
   * @param {Array} data filter value data
   * @param {Object} selectedItem
   */
  findSelectedItem(data: Array<{}>, selectedItem: {}) {
    let item = {
      selected: {},
      parents: []
    };

    const findItem = (data, selectedItem, item, parentNode) => {
      let i;

      for (i = 0; i < data.length; i++) {
        if (!isNil(item.selected.value)) {
          break;
        }

        const d = data[i];

        if (
          d.value === selectedItem.value ||
          (parentNode && `${parentNode.value}\\${d.value}` === selectedItem.value)
        ) {
          item.selected = d;
        } else {
          if (d.nodes && d.nodes.length) {
            item.parents.push(d);
            findItem(d.nodes, selectedItem, item, d);
            if (isNil(item.selected.value)) {
              item.parents = removeFn(item.parents, d);
            }
          }
        }
      }
    };

    const removeFn = (parents, d) => {
      return remove(item => {
        return item === d;
      }, parents);
    };

    findItem(data, selectedItem, item);

    return item;
  }

  // Update for All checkbox
  updateAll(data: Array<{}>, isChecked: boolean) {
    forEach(d => {
      d.checked = isChecked;
      if (d.nodes && d.nodes.length) {
        this.updateAll(d.nodes, isChecked);
      }
    }, data);

    return data;
  }

  getComputedValue(node, key, parent) {
    return isNil(parent) || parent[key] === ALL_TEXT || !parent[key] ? node[key] : `${parent[key]}\\${node[key]}`;
  }

  // Fill selected values that are sent to the server side
  fillSelectedValues(data: Array<{}>, selectedValues: {}, parent: any) {
    forEach(node => {
      if (node.nodes && node.nodes.length) {
        this.fillSelectedValues(node.nodes, selectedValues, node);
      } else if (node.checked) {
        const value = this.getComputedValue(node, 'value', parent);
        const text = this.getComputedValue(node, 'text', parent);
        selectedValues.push({ text, value });
      }
    }, data);
  }

  // Fill values that are displayed in the UI
  fillDisplayedValues(data: Array<{}>, displayedValues: {}, parent: any) {
    forEach(node => {
      if (node.checked) {
        const value = this.getComputedValue(node, 'value', parent);
        const text = this.getComputedValue(node, 'text', parent);
        displayedValues.push({ text, value });
      } else if (node.nodes && node.nodes.length) {
        this.fillDisplayedValues(node.nodes, displayedValues, node);
      }
    }, data);
  }

  // Check all options is selected
  isAllOptionSelected(data: Array<{}>) {
    let result = true;

    forEach(d => {
      if (!d.checked) {
        result = false;
        return false; // Break the loop
      }

      if (d.nodes && d.nodes.length) {
        result = result && this.isAllOptionSelected(d.nodes);
      }
    }, data);

    return result;
  }

  // Check all row
  checkAllSelected(data: Array<{}>) {
    const allNode = head(data);
    const allOptionsSelected = this.isAllOptionSelected(get('nodes', allNode));

    if (allOptionsSelected) {
      let allItem = this.findSelectedItem(data, { value: ALL_TEXT }).selected;
      allItem.checked = true;
    }
  }

  @action
  handleChangeTreeSelect = (item: {}) => {
    const data = this.observableObject.selectedItems;
    let selectedValues = [];
    let displayedValues = [];

    if (item.checked) {
      if (item.value === ALL_TEXT) {
        this.updateAll(data, true);
      } else {
        const selectedItem = this.findSelectedItem(data, item);
        selectedItem.selected.checked = true;
        // Update parent node
        forEach(
          function(parent) {
            const isAllSelected = this.isAllOptionSelected(get('nodes', parent));
            isAllSelected && (parent.checked = true);
          }.bind(this),
          reverse(selectedItem.parents)
        );
        // Update children node
        this.updateAll([selectedItem.selected], true); 
      }
    } else {
      if (item.value === ALL_TEXT) {
        this.updateAll(data, false);
      } else {
        const selectedItem = this.findSelectedItem(data, item);
        // Update parent node
        forEach(parent => {
          parent.checked = false;
        }, selectedItem.parents);
        // Update children node
        this.updateAll([selectedItem.selected], false);
      }
    }

    this.checkAllSelected(data);
    this.fillSelectedValues(data, selectedValues);
    this.fillDisplayedValues(data, displayedValues);

    // get the value with format [parentValue]//[childValue] as string format only. Back-end is accepting this format.
    const onChangeValue = flatMap('value', selectedValues);
    this.props.onChange(onChangeValue);
    this.observableObject.displayedValues = displayedValues;
  };

  @action
  onChangeSelect = (items: Array<{}>) => {
    const data = this.observableObject.selectedItems;
    let selectedValues = [];
    let displayedValues = [];

    this.updateAll(data, false);

    if (items.length) {
      // Check selected items
      forEach(item => {
        let dataItem = this.findSelectedItem(data, { value: get('id', item) });
        this.updateAll([dataItem.selected], true);
      }, items);
      // Get selected values after uncheck children
      this.fillSelectedValues(data, selectedValues);
      this.fillDisplayedValues(data, displayedValues);
    }

    // get the value with format [parentValue]//[childValue] as string format only. Back-end is accepting this format.
    const onChangeValue = flatMap('value', selectedValues);
    this.props.onChange(onChangeValue);
    this.observableObject.displayedValues = displayedValues;
  };

  setComboTreeDom = (dom: HTMLElement) => {
    this.dom.comboTreeDom = dom;
  };

  render() {
    const { applyFormater, disabled, renderInContainer } = this.props;
    return (
      <div className='combo-tree'>
          <div className='combo' ref={this.setComboTreeDom}>
            <Input type='text' className='form-control' />
            <button className='btn combo-picker' onMouseDown={this.handleShowTreeView} disabled={disabled}>
              <i className='icon-triangle-down' />
            </button>
            <SelectedValue
              applyFormater={applyFormater}
              observableObject={this.observableObject}
              disabled={disabled}
              onChangeSelect={this.onChangeSelect}
            />
          </div>
          <TreeDropDown
            masterDom={this.dom}
            hideTreeView={this.hideTreeView}
            applyFormater={applyFormater}
            observableObject={this.observableObject}
            disabled={disabled}
            handleChangeTreeSelect={this.handleChangeTreeSelect}
            renderInContainer={renderInContainer}
          />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/OperatorValue/Izenda.OperatorValueComboTree.js